import React, {useEffect, useImperativeHandle, useState} from "react";
import {CustomCheckbox, InputSaveEdit} from "../../../shared";
import {FormattedMessage, useIntl} from "react-intl";
import {isMobileDevice} from "../../../../utils/utils";
import functions from "../../../../utils/functionsFirebase";

const RoleForm = React.forwardRef((props, ref) => {

    const intl = useIntl();

    const [roleName, setRoleName] = useState(props.role?.roleName ?? "");
    const [acceptUsers, setAcceptUsers] = useState(!!props.role?.acceptUsers);
    const [inviteUsers, setInviteUsers] = useState(!!props.role?.inviteUsers);
    const [manageRoles, setManageRoles] = useState(!!props.role?.manageRoles);
    const [publishFiles, setPublishFiles] = useState(!!props.role?.publishFiles);
    const [modifyInfo, setModifyInfo] = useState(!!props.role?.modifyInfo);
    const [kickUsers, setKickUsers] = useState(!!props.role?.kickUsers);
    const [manageTags, setManageTags] = useState(!!props.role?.manageTags);
    const [administrator, setAdministrator] = useState(!!props.role?.administrator);
    const [tagOwnFiles, setTagOwnFiles] = useState(!!props.role?.tagOwnFiles);
    const [deletePosts, setDeletePosts] = useState(!!props.role?.deletePosts);

    useEffect(() => {
        props.setValidRole(!!roleName);
    }, [roleName]);

    useEffect(() => {
        setRoleName(props.role?.roleName ?? "");
        setAcceptUsers(!!props.role?.acceptUsers);
        setInviteUsers(!!props.role?.inviteUsers);
        setManageRoles(!!props.role?.manageRoles);
        setPublishFiles(!!props.role?.publishFiles);
        setModifyInfo(!!props.role?.modifyInfo);
        setKickUsers(!!props.role?.kickUsers);
        setManageTags(!!props.role?.manageTags);
        setAdministrator(!!props.role?.administrator);
        setTagOwnFiles(!!props.role?.tagOwnFiles);
        setDeletePosts(!!props.role?.deletePosts);
    }, [props.role]);

    async function saveRole() {
        const saveRole = functions.httpsCallable('saveRole');
        const role = {
            id: props.role.id,
            roleName,
            acceptUsers,
            inviteUsers,
            manageRoles,
            publishFiles,
            modifyInfo,
            kickUsers,
            manageTags,
            administrator,
            tagOwnFiles,
            deletePosts,
            order: 0,
            everyone: props.role.everyone,
            groupId: props.groupId
        };
        const res = await saveRole({groupId: props.groupId, role});
    }

    useImperativeHandle(ref, () => ({
        // Method to save the role that is being edited
        saveRole: () => {
            return saveRole();
        },
    }));

    if (!props.role) {
        return;
    }

    return (
        <div>
            <div className="mb-2">
                <InputSaveEdit id="roleName" onSave={value => setRoleName(value)} inputValue={roleName}
                               alwaysEditing={true} maxLength={20} required={true}
                               label={intl.formatMessage({id: "roles.role-form.field.name"})}/>
            </div>

            <h4 className="ms-3">
                <FormattedMessage id="roles.role-form.field.basic-rights"/>
            </h4>

            <div className="mb-2">
                <CustomCheckbox id="publishFilesRoleToggle" value={publishFiles}
                                setValue={value => setPublishFiles(value)}
                                inline={true} labelClass="col-6"
                                label={intl.formatMessage({id: "roles.role-form.field.right-publishFiles"})}/>
            </div>
            <div className="mb-2">
                <CustomCheckbox id="tagOwnFilesRoleToggle" value={tagOwnFiles}
                                setValue={value => setTagOwnFiles(value)}
                                inline={true} labelClass="col-6"
                                label={intl.formatMessage({id: "roles.role-form.field.right-tagOwnFiles"})}/>
            </div>

            <h4 className="ms-3">
                <FormattedMessage id="roles.role-form.field.user-rights"/>
            </h4>

            <div className="mb-2">
                <CustomCheckbox id="acceptUsersRoleToggle" value={acceptUsers} setValue={value => setAcceptUsers(value)}
                                inline={true} labelClass="col-6"
                                label={intl.formatMessage({id: "roles.role-form.field.right-acceptUsers"})}/>
            </div>

            <div className="mb-2">
                <CustomCheckbox id="kickUsersRoleToggle" value={kickUsers} setValue={value => setKickUsers(value)}
                                inline={true} labelClass="col-6"
                                label={intl.formatMessage({id: "roles.role-form.field.right-kickUsers"})}/>
            </div>

            <div className="mb-2">
                <CustomCheckbox id="inviteUsersRoleToggle" value={inviteUsers} setValue={value => setInviteUsers(value)}
                                inline={true} labelClass="col-6"
                                label={intl.formatMessage({id: "roles.role-form.field.right-inviteUsers"})}/>
            </div>


            <h4 className="ms-3">
                <FormattedMessage id="roles.role-form.field.group-rights"/>
            </h4>

            <div className="mb-2">
                <CustomCheckbox id="manageRolesRoleToggle" value={manageRoles} setValue={value => setManageRoles(value)}
                                inline={true} labelClass="col-6"
                                label={intl.formatMessage({id: "roles.role-form.field.right-manageRoles"})}/>
            </div>

            <div className="mb-2">
                <CustomCheckbox id="modifyInfoRoleToggle" value={modifyInfo} setValue={value => setModifyInfo(value)}
                                inline={true} labelClass="col-6"
                                label={intl.formatMessage({id: "roles.role-form.field.right-modifyInfo"})}/>
            </div>

            <div className="mb-2">
                <CustomCheckbox id="manageTagsRoleToggle" value={manageTags} setValue={value => setManageTags(value)}
                                inline={true} labelClass="col-6"
                                label={intl.formatMessage({id: "roles.role-form.field.right-manageTags"})}/>
            </div>

            <div className="mb-2">
                <CustomCheckbox id="deletePostsToggle" value={deletePosts} setValue={value => setDeletePosts(value)}
                                inline={true} labelClass="col-6"
                                label={intl.formatMessage({id: "roles.role-form.field.right-deletePosts"})}/>
            </div>

            <h4 className="ms-3">
                <FormattedMessage id="roles.role-form.field.admin-rights"/>
            </h4>

            <div className="mb-2">
                <CustomCheckbox id="administratorRoleToggle" value={administrator} inline={true}
                                setValue={value => setAdministrator(value)} labelClass="col-6"
                                label={
                                    <span>
                                        {intl.formatMessage({id: "roles.role-form.field.right-administrator"})}
                                        <i className="fa-solid fa-a ms-2 text-warning" title="Admin"></i>
                                    </span>}/>
                <small>
                    <FormattedMessage id="roles.role-form.field.right-administrator-info"/>
                </small>
            </div>

        </div>
    )
});

export default RoleForm;
